import React from 'react';

// Visual skin per template — header, accent, container classes.
// Used by RealisticCertificatePreview to make the live preview MATCH the
// selected template card design.

const skins = {
  classic: {
    container: 'bg-white border-2 border-gray-800',
    accent: '#111827',
    header: ({ hospitalName, doctorName, registrationNumber }) => (
      <div className="border-b-2 border-gray-800 pb-3 mb-6">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 tracking-tight">
            {doctorName || 'Dr. R. Sharma'}
          </h2>
          <p className="text-xs text-gray-700 mt-1 font-serif italic">
            MBBS, MD (General Medicine)
          </p>
          <p className="text-[11px] text-gray-600 mt-0.5">
            Reg. No.: {registrationNumber || 'MCI/HR/2026/XXXXX'} · Consulting Hours: 10AM – 8PM
          </p>
          {hospitalName && (
            <p className="text-xs text-gray-700 mt-2 font-medium">{hospitalName}</p>
          )}
        </div>
      </div>
    ),
    footerNote: null,
  },

  modern: {
    container: 'bg-white border border-gray-200 shadow-md',
    accent: '#0d9488',
    header: ({ hospitalName, doctorName, registrationNumber }) => (
      <div className="mb-6 -mx-8 -mt-8">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-8 py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-teal-600 text-2xl font-bold flex-shrink-0">
            ✚
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold tracking-tight">{hospitalName || 'City Care Hospital'}</h2>
            <p className="text-xs text-teal-100 mt-0.5">multispeciality &amp; diagnostic care</p>
          </div>
          <div className="text-right text-[11px] text-teal-100 hidden sm:block">
            <p className="font-medium text-white">{doctorName || 'Dr. Doctor Name'}</p>
            <p>Reg. {registrationNumber || 'XXXXXX'}</p>
          </div>
        </div>
      </div>
    ),
    footerNote: null,
  },

  'govt-hospital': {
    container: 'bg-white border border-gray-300',
    accent: '#138808',
    header: ({ hospitalName, doctorName, registrationNumber }) => (
      <div className="mb-6 -mx-8 -mt-8">
        {/* Tricolor band */}
        <div className="flex h-2">
          <div className="flex-1 bg-orange-500" />
          <div className="flex-1 bg-white border-y border-gray-200 relative">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-blue-900" />
          </div>
          <div className="flex-1 bg-green-700" />
        </div>
        <div className="px-8 py-5 text-center border-b-2 border-gray-700">
          <p className="text-[10px] uppercase tracking-widest text-gray-600 font-medium">
            Government of India · Ministry of Health &amp; Family Welfare
          </p>
          <h2 className="text-xl font-serif font-bold text-gray-900 mt-1 uppercase tracking-wide">
            {hospitalName || 'Government Civil Hospital'}
          </h2>
          <p className="text-[11px] text-gray-700 mt-1">
            Office of the Chief Medical Officer
          </p>
          <div className="mt-2 flex justify-between text-[11px] text-gray-700 px-2">
            <span>CMO No.: GH/2026/____</span>
            <span>{doctorName || 'Dr. Doctor Name'} · {registrationNumber || 'Reg. XXXXX'}</span>
          </div>
        </div>
      </div>
    ),
    footerNote: 'Issued under Government of India · MoHFW guidelines',
  },

  'private-hospital': {
    container: 'bg-white border border-gray-200 shadow-sm',
    accent: '#1e3a8a',
    header: ({ hospitalName, doctorName, registrationNumber }) => (
      <div className="mb-6 -mx-8 -mt-8">
        <div className="bg-gradient-to-r from-blue-900 to-blue-600 text-white px-8 py-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center text-blue-900 text-2xl font-bold flex-shrink-0">
            H+
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold tracking-tight uppercase">{hospitalName || 'Apollo+ Hospital'}</h2>
            <p className="text-[11px] text-blue-100 mt-0.5 uppercase tracking-wider">Multi-Speciality Hospital</p>
            <p className="text-[11px] text-blue-200 mt-0.5">
              {doctorName || 'Dr. Doctor Name'} · Reg. No.: {registrationNumber || 'PVT/HOS/9821'}
            </p>
          </div>
          {/* faux QR */}
          <div className="w-12 h-12 bg-white p-1 hidden sm:grid grid-cols-3 gap-0.5 flex-shrink-0">
            {QR_CELLS.map((cell) => (
              <div key={cell.id} className={cell.on ? 'bg-blue-900' : 'bg-white'} />
            ))}
          </div>
        </div>
      </div>
    ),
    footerNote: null,
  },

  'private-clinic': {
    container: 'bg-amber-50/40 border border-amber-200',
    accent: '#7c2d12',
    header: ({ hospitalName, doctorName, registrationNumber }) => (
      <div className="mb-6 -mx-8 -mt-8">
        <div className="h-1.5 bg-amber-500" />
        <div className="px-8 py-5 flex items-start justify-between border-b border-amber-200">
          <div>
            <h2 className="text-xl font-serif font-bold text-amber-900">
              {doctorName || 'Dr. P. Bansal'}
            </h2>
            <p className="text-xs text-amber-800 mt-0.5 italic">M.B.B.S., D.C.H.</p>
            <p className="text-[11px] text-amber-700 mt-0.5">Reg.: {registrationNumber || 'MMC/8821'}</p>
          </div>
          <div className="text-right">
            <h3 className="text-lg font-serif font-bold text-amber-900 uppercase tracking-wide">
              {hospitalName || 'Sunrise Family Clinic'}
            </h3>
            <p className="text-[11px] text-amber-700 mt-0.5">Family Medicine · Paediatrics</p>
            <p className="text-[11px] text-amber-700 mt-0.5">Open: 10 AM – 8 PM</p>
          </div>
        </div>
      </div>
    ),
    footerNote: null,
  },
};

export const getTemplateSkin = (templateType = 'classic') => skins[templateType] || skins.classic;

const TemplateSkin = ({ templateType, hospitalName, doctorName, registrationNumber, children }) => {
  const skin = getTemplateSkin(templateType);
  return (
    <div className={`rounded-lg p-8 shadow-sm ${skin.container}`} id="certificate-preview" style={{ fontFamily: 'system-ui' }}>
      {skin.header({ hospitalName, doctorName, registrationNumber })}
      {children}
      {skin.footerNote && (
        <p className="mt-4 text-center text-[10px] uppercase tracking-wider" style={{ color: skin.accent }}>
          {skin.footerNote}
        </p>
      )}
    </div>
  );
};

export default TemplateSkin;
